import { Descriptions, Space } from '@arco-design/web-react';
import DetailSection from '../DetailSection';
import EmptyValue from '../EmptyValue';
import PillTag from '../PillTag';
import StatusTag from '../StatusTag';
import { fmtTime, maskPhone, parseJsonArray } from '../../utils/format';
import { ticketSourceLabel } from '../../utils/labels';
import { getIntentName } from '../../utils/intentName';
import { isTicketOverdue, TICKET_TYPE_OPTIONS, type Ticket } from '../../api/ticket';

export interface TicketBasicInfoPanelProps {
  /** 工单详情（基础信息段的唯一数据源） */
  detail: Ticket;
}

/**
 * 工单详情「基本信息」分节：固定字段只读展示（状态/类型/分类/标签/来源/时间/SLA 等）。
 * 分类 code 走 getIntentName 还原中文，来源/状态标签复用全局出口，均为纯展示。
 */
export default function TicketBasicInfoPanel({ detail }: TicketBasicInfoPanelProps) {
  return (
    /* 字段列表的只读分节标题：下方沟通/附件/关联由页签自带标题，内容/描述两段自带字段名标签，
       故只在此处补与表单侧同一套标题，其余块不再套一层 */
    <DetailSection title="基本信息">
      <Descriptions
        column={2}
        data={[
          { label: '状态', value: <StatusTag value={detail.status} /> },
          { label: '优先级', value: <StatusTag value={detail.priority} /> },
          { label: '类型', value: TICKET_TYPE_OPTIONS.find((t) => t.value === detail.type)?.label || detail.type || <EmptyValue /> },
          {
            label: '分类',
            value: (() => {
              if (!detail.category) return <EmptyValue />;
              return typeof detail.category === 'string' ? (
                detail.category.split(',').map((s) => s.trim()).filter(Boolean).map((s, i) => (
                  <PillTag key={i} color="arcoblue">{getIntentName(s)}</PillTag>
                ))
              ) : (
                detail.category
              );
            })(),
          },
          {
            // 结构化标签（PillTag 平铺）：detail.tags 来自 Ticket.Tags JSON 数组，
            label: '标签',
            value: (() => {
              const tags = parseJsonArray(detail.tags);
              if (tags.length === 0) return <EmptyValue />;
              return (
                <Space size={4} wrap>
                  {tags.map((t, i) => (
                    <PillTag
                      key={`${i}-${t}`}
                      color={i % 2 === 0 ? 'arcoblue' : 'cyan'}
                      size="sm"
                    >
                      {t}
                    </PillTag>
                  ))}
                </Space>
              );
            })(),
          },
          {
            label: '来源',
            value: (() => {
              const s = ticketSourceLabel(detail.source);
              // AI 来源用 AI 电光青系 cyan（全站无紫色系）
              return detail.source?.startsWith('ai_') ? <PillTag color="cyan">{s}</PillTag> : s;
            })(),
          },
          {
            label: '审核流程',
            value: detail.workflow_template_name ? (
              detail.workflow_template_name
            ) : detail.workflow_instance_id ? (
              `#${detail.workflow_instance_id}`
            ) : (
              <EmptyValue />
            ),
          },
          {
            label: '需回访',
            value: detail.follow_up ? '是（关闭后自动生成回访外呼）' : '否',
          },
          {
            label: '客户',
            value: detail.customer_name ? (
              `${detail.customer_name}${detail.customer_phone ? `（${maskPhone(detail.customer_phone)}）` : ''}`
            ) : (
              <EmptyValue />
            ),
          },
          { label: '指派人', value: detail.assignee_name || <EmptyValue /> },
          { label: '创建时间', value: fmtTime(detail.created_at) },
          {
            label: 'SLA 截止',
            value: detail.sla_due_at ? (
              <Space size={4}>
                <span style={isTicketOverdue(detail) ? { color: 'var(--bcc-danger)', fontWeight: 600 } : undefined}>
                  {fmtTime(detail.sla_due_at)}
                </span>
                {isTicketOverdue(detail) && <PillTag color="red">已超时</PillTag>}
              </Space>
            ) : (
              <EmptyValue />
            ),
          },
          { label: '解决时间', value: detail.resolved_at ? fmtTime(detail.resolved_at) : <EmptyValue /> },
          { label: '完成时间', value: detail.completed_at ? fmtTime(detail.completed_at) : <EmptyValue /> },
          { label: '关闭时间', value: detail.closed_at ? fmtTime(detail.closed_at) : <EmptyValue /> },
          {
            label: '负责技能组',
            value: detail.responsible_skill_group_id ? (
              <span className="bcc-mono">#{detail.responsible_skill_group_id}</span>
            ) : (
              <EmptyValue />
            ),
          },
        ]}
      />
    </DetailSection>
  );
}
